import * as React from 'react';
import type {ChatMessage, ActionEvent} from 'src/components/agentic-ui';
import {subChatStore, getSubChatWindowStatus} from './sub-chat-store';
import type {WidgetPart} from 'src/components/agentic-ui/types';
import {useQueryClient, skipToken} from '@tanstack/react-query';
import {
  getMessageHistoryQueryKey,
  FULLSCREEN_MODE_KEY,
  POSITION_PERSIST_KEY,
  DEFAULT_CONVERSATION_NAME,
  CONVERSATION_LIST_QUERY_KEY,
} from 'src/utils/ask-ai';
import type {ApiPostOptions} from 'src/utils/redux-api';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import logger from 'src/utils/logger';
import useAgencyConfig from 'src/hooks/useAgencyConfig';
import {
  ChatPanelLexical,
  type ChatPanelHandle,
  type PanelMode,
} from 'src/components/agentic-ui/chat-panel';
import {selectIsAuthed} from 'src/selectors/accounts';
import {
  useRespondToAutoAttach,
  useRequestToAutoAttach,
  useHeaderSlot,
} from 'src/components/agentic-ui/use-auto-attach';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';
import {useApiMutation} from 'src/hooks/useApiMutation';

import {AgenticSuggestions} from 'src/components/agentic-ui/agentic-suggestions';
import {PastConversationsView} from 'src/components/agentic-ui/past-conversations-view';
import {useApiQuery} from 'src/hooks/useApiQuery';
import * as reduxApiAskAi from 'src/utils/redux-api-ask-ai';
import css from './ask-ai.css';
import {
  registerChatPanel,
  useAskAIProduct,
  useAskAIThreadId,
  useShouldFetchStarterPrompts,
  setThreadId as setThreadIdInStore,
  getThreadId,
  initNewThread,
} from './ask-ai-store';
import {runPreSendCollectors} from 'src/components/agentic-ui/context-collectors';
import {publishAgenticEvent} from 'src/components/agentic-ui/agentic-ui-event-bus';
import {useHistory, useLocation} from 'src/rerouter';

import {useAskAiPositioningContext} from './useAskAiPositioningContext';


type ThreadCreatePayload = {
  name?: string;
  context: {
    page_context: {
      product: string;
    };
  };
};

export const AskAI = () => {
  const {t} = useLabelTranslation();
  const {
    isExtension,
    isClientIframe,
    isSenseAiIframe,
    isTrmIframe,
    hasContextInUrl,
  } = useEnvironmentContext();
  const queryClient = useQueryClient();
  const chatPanelRef = React.useRef<ChatPanelHandle | null>(null);
  // Mode + open state the panel was in right before entering fullscreen, so
  // closing fullscreen restores it exactly instead of defaulting to floating.
  // Null when fullscreen was entered directly (e.g. a fresh load of the
  // fullscreen URL), in which case close falls back to minimized.
  const preFullscreenStateRef = React.useRef<{
    mode: PanelMode;
    open: boolean;
  } | null>(null);
  const dispatch = useDispatch();
  const {headerSlot} = useRespondToAutoAttach(chatPanelRef);
  const isAuthed = useSelector(selectIsAuthed);
  const askAiEnabled = Boolean(useAgencyConfig('graceEnabled'));
  const product = useAskAIProduct();
  const threadId = useAskAIThreadId();
  const shouldFetchStarterPrompts = useShouldFetchStarterPrompts();
  const history = useHistory();
  const location = useLocation();
  const isFullScreenChatExperience =
    location.query[FULLSCREEN_MODE_KEY] === 'true';
  const {elevation: askAiElevation, leftMargin} = useAskAiPositioningContext();
  const [showHistoryView, setShowHistoryView] = React.useState(false);

  const {data: threadData} = useApiQuery<{
    messages: ChatMessage[];
    name?: string;
    sub_chat_window_active?: boolean;
  }>(`conversation/${threadId}`, {
    queryOptions: {
      queryKey: threadId ? getMessageHistoryQueryKey(threadId) : skipToken,
      enabled: !!threadId,
    },
    apiOptions: {
      method: 'GET',
      options: {
        apiPath: '/grace/api/v1/',
      },
    },
  });
  const messageHistory = threadData?.messages || [];
  const conversationName = threadData?.name || DEFAULT_CONVERSATION_NAME;

  // Restore must only consume the FIRST fetch per thread (the page-refresh
  // case). Later refetches (e.g. the rename invalidation) must never re-seed
  // a sub-chat the user already abandoned/completed in this session.
  const hasRestoredSubChatRef = React.useRef(false);

  React.useEffect(() => {
    // Restore docked sub-chat on page refresh / when an old conversation is
    // opened (doc §4.12). Only seed when the store is idle so we never clobber
    // a live, in-session sub chat.
    if (!threadData || hasRestoredSubChatRef.current) {
      return;
    }
    hasRestoredSubChatRef.current = true;
    if (subChatStore.getState().status !== 'absent') {
      return;
    }
    const msgs = threadData.messages ?? [];

    // The questionnaire is only "pending" when the conversation ends with an
    // agent message that still carries the docked widget — i.e. the agent asked
    // and the user has neither answered nor abandoned it. A trailing user
    // message means they already responded/abandoned; a later agent message
    // (e.g. an inline result) means the flow has moved on. In both cases we must
    // NOT reopen the widget.
    //
    // We deliberately derive this from the message history rather than the
    // backend `sub_chat_window_active` flag: that flag stays true after a flow
    // is completed/abandoned, which was causing finished conversations to
    // re-pop the questionnaire every time they were opened.
    const lastMsg = msgs[msgs.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') {
      return;
    }
    const dockedWidget = lastMsg.parts.find(
      (p): p is WidgetPart =>
        p.type === 'widget' && p.data.widget_visibility === 'docked',
    );
    if (!dockedWidget) {
      return;
    }

    // latest user message status decides max vs min
    let restoredStatus: 'max' | 'min' = 'min';
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i].role === 'user') {
        restoredStatus =
          msgs[i].sub_chat_window_status === 'max' ? 'max' : 'min';
        break;
      }
    }

    // The trailing sub-chat run begins after the last message that closed a
    // prior run (sub_chat_window_status 'max'). The docking starter's name is
    // the first assistant agent_display_name in that run — NOT the trailing
    // message, which may belong to a later agent (e.g. one answered while
    // minimized) or omit the field. Mirrors the live capture-at-start rule.
    let runStart = 0;
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i].sub_chat_window_status === 'max') {
        runStart = i + 1;
        break;
      }
    }
    let restoredAgentName: string | null = null;
    for (let i = runStart; i < msgs.length; i += 1) {
      if (msgs[i].role === 'assistant' && msgs[i].agent_display_name) {
        restoredAgentName = msgs[i].agent_display_name ?? null;
        break;
      }
    }

    subChatStore.getState().seed({
      status: restoredStatus,
      dockedWidget,
      sourceMessageId: lastMsg.id,
      agentDisplayName: restoredAgentName,
    });
  }, [threadData]);

  //if threadId is not there make an api call using useApiMutation to create a thread and set it in local storage
  const {mutateAsync: createThread, isPending: _isUpserting} = useApiMutation({
    mutationFn: async (payload: ThreadCreatePayload) => {
      return dispatch(
        reduxApiAskAi.post(
          'conversation',
          payload,
          undefined,
          {} as ApiPostOptions,
        ),
      );
    },
  });

  const handleCreateThread = async (): Promise<string> => {
    const thread = await createThread({
      context: {
        page_context: {
          product,
        },
      },
    });
    setThreadIdInStore(thread.id);
    return thread.id;
  };

  const handleClearThread = () => {
    // TODO:(diwakersurya)for now, default to workflow to get proper starter prompts
    // FIXME:(diwakersurya)it should remember the product from askai store.
    initNewThread({source: 'workflow', shouldFetchStarterPrompts: true});
    //initNewThread({source: product, shouldFetchStarterPrompts: true});
  };

  // ── Conversation rename ──
  const isManuallyRenamed = React.useRef(false);
  const userMessageCountRef = React.useRef(0);

  React.useEffect(() => {
    isManuallyRenamed.current = false;
    userMessageCountRef.current = 0;
    // Re-arm the one-shot restore for the new thread's first fetch.
    hasRestoredSubChatRef.current = false;
    subChatStore.getState().reset();
  }, [threadId]);

  React.useEffect(() => {
    userMessageCountRef.current = messageHistory.filter(
      (m) => m.role === 'user',
    ).length;
  }, [messageHistory]);

  const {mutateAsync: renameConversation} = useApiMutation({
    mutationFn: async ({name}: {name: string}) => {
      if (!threadId) {
        return;
      }
      return dispatch(reduxApiAskAi.patch(`conversation/${threadId}`, {name}));
    },
  });

  const handleConversationNameChange = React.useCallback(
    async (name: string) => {
      if (!threadId) {
        return;
      }
      isManuallyRenamed.current = true;
      await renameConversation({name});
      queryClient.setQueryData(
        getMessageHistoryQueryKey(threadId),
        (old: {messages: ChatMessage[]; name?: string} | undefined) => ({
          ...old,
          name,
        }),
      );
      queryClient.invalidateQueries({
        queryKey: [...CONVERSATION_LIST_QUERY_KEY],
      });
    },
    [threadId, renameConversation, queryClient],
  );

  const handleMessageSent = React.useCallback(() => {
    userMessageCountRef.current += 1;
    if (
      userMessageCountRef.current >= 5 &&
      !isManuallyRenamed.current &&
      threadId
    ) {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: getMessageHistoryQueryKey(threadId),
        });
      }, 2000);
    }
  }, [threadId, queryClient]);

  const getStreamUrl = async () => {
    // Read the thread id from the store, not this render's `threadId` —
    // use-chat invokes this through a ref, so a send fired right after
    // initNewThread() (startNewConversation) would otherwise see the stale
    // pre-reset id and land the message in the old conversation.
    const id = getThreadId() ?? (await handleCreateThread());

    return `/grace/api/v1/conversation/${id}/chat`;
  };

  React.useEffect(() => {
    const handle = chatPanelRef.current;
    if (!handle) {
      return;
    }
    if (isFullScreenChatExperience) {
      // Remember the mode + open state we're coming from so close can restore
      // it exactly. Guard against re-capturing 'fullscreen' (e.g. effect
      // re-runs, or a direct load that already started in fullscreen — leaves
      // the ref null).
      if (handle.currentMode && handle.currentMode !== 'fullscreen') {
        preFullscreenStateRef.current = {
          mode: handle.currentMode,
          open: handle.currentOpen ?? false,
        };
      }
      handle.setMode('fullscreen');
      handle.setOpen(true);
      return;
    }
    if (handle.currentMode === 'fullscreen') {
      const previous = preFullscreenStateRef.current;
      preFullscreenStateRef.current = null;
      if (previous) {
        // Return to the exact prior mode + open/closed state.
        handle.setMode(previous.mode);
        handle.setOpen(previous.open);
      } else {
        // No prior state (entered fullscreen directly) → fall back to minimized.
        handle.setMode('floating');
        handle.setOpen(false);
      }
    }
  }, [isFullScreenChatExperience]);

  const handleSubmitFeedback = React.useCallback(
    async (
      messageId: string,
      feedback_rating: number,
      feedback_comment: string,
    ) => {
      await dispatch(
        reduxApiAskAi.patch(`conversation/message/${messageId}/feedback`, {
          rating: feedback_rating,
          comment: feedback_comment,
        }),
      );
      if (!threadId) {
        return;
      }
      // Update the query cache after submitting feedback so that we don't refetch
      queryClient.setQueryData(
        getMessageHistoryQueryKey(threadId),
        (oldData: {messages: ChatMessage[]}) => {
          if (!oldData || !oldData.messages) {
            return oldData;
          }
          return {
            ...oldData,
            messages: oldData.messages.map((message) =>
              message.id === messageId
                ? {...message, feedback_rating, feedback_comment}
                : message,
            ),
          };
        },
      );
      //also update the local streamed messages store
      if (chatPanelRef.current) {
        chatPanelRef.current.updateMessageFeedback(
          messageId,
          feedback_rating,
          feedback_comment,
        );
      }
    },
    [dispatch, threadId],
  );

  const handleClose = React.useCallback(() => {
    if (isFullScreenChatExperience) {
      const sourceurl = location.query.sourceurl ?? null;
      if (!sourceurl) {
        logger.error(
          'Source is required to exit out of fullscreen chat experience',
        );
        history.replace('/');
        return;
      }
      history.replace(sourceurl);
    }
  }, [history, isFullScreenChatExperience, location]);

  //NOTE:(diwakersurya) Do not show AskAI in extension, client iframe, or sense ai iframe.
  // hasContextInUrl is final if the url contains context, that means we are loading in a
  // separate iframe/environment other than app. we don't want to open sense ai there.
  if (
    !isAuthed ||
    !askAiEnabled ||
    isExtension ||
    isClientIframe ||
    isSenseAiIframe ||
    isTrmIframe ||
    hasContextInUrl
  ) {
    return null;
  }
  return (
    <>
      <ChatPanelLexical
        ref={(node) => {
          chatPanelRef.current = node;
          registerChatPanel(node);
        }}
        attachChatTo="#chat-container"
        attachHeaderTo="#chat-header-container"
        defaultMode={isFullScreenChatExperience ? 'fullscreen' : 'floating'}
        placeholder={t('Ask_me_anything', 'Ask me anything')}
        positionPersistKey={POSITION_PERSIST_KEY}
        renderStartScreen={({publishMessageAction}) => {
          if (shouldFetchStarterPrompts) {
            return (
              <AgenticSuggestions
                publishMessageAction={
                  publishMessageAction as (action: ActionEvent) => void
                }
              />
            );
          }
          return null;
        }}
        messageHistory={messageHistory}
        onSubmitFeedback={handleSubmitFeedback}
        //we need this prop in attached mode to allow host to provide
        //custom content in the header slot
        headerSlot={headerSlot}
        height={400}
        chatOptions={{
          // resolve SSE URL lazily (creates thread on first send if needed)
          getStreamUrl,
          headers: {
            Authorization: `Bearer ${'test'}`,
          },
          collectMessageContext: () =>
            runPreSendCollectors({history}, undefined),
          // Server expects the sub-chat window status with each send (doc §5.1),
          // resolved at send-time since it can change between renders.
          getRequestExtras: () => ({
            sub_chat_window_status: getSubChatWindowStatus(),
          }),
          // Open/re-dock when a docked widget arrives — handled as a stream
          // event (no Effect watching derived state). Only fires for live
          // stream parts, so restored history (page refresh) is untouched.
          onDockedWidget: (widget, messageId, agentDisplayName) =>
            subChatStore.getState().open(widget, messageId, agentDisplayName),
          // Resolve the sub-chat to ABSENT when a MAX submit is answered by a
          // terminal, non-docked reply. Idempotent (safe on terminate + close).
          onResponseComplete: (messages) => {
            if (subChatStore.getState().status !== 'max') {
              return;
            }
            const last = messages[messages.length - 1];
            const lastHasDocked = !!last?.parts?.some(
              (p) =>
                p.type === 'widget' &&
                (p as WidgetPart).data.widget_visibility === 'docked',
            );
            if (last?.role === 'assistant' && !lastHasDocked) {
              subChatStore.getState().reset();
            }
          },
          // A terminal stream failure must not strand the user in the MAX
          // "processing" state (composer hidden, no widget, no close button).
          // Scoped to exactly that state: a still-docked questionnaire (MAX +
          // widget) and the MIN bar keep their state across errors.
          onResponseError: () => {
            const s = subChatStore.getState();
            if (s.status === 'max' && !s.dockedWidget) {
              s.reset();
            }
          },
          publishMessageAction: (action: ActionEvent) => {
            try {
              const {product, event, payload, dedupeKey} = (action || {}) as {
                product: unknown;
                event: unknown;
                payload?: unknown;
                dedupeKey?: string;
              };
              if (event) {
                const emit = publishAgenticEvent as unknown as (
                  product: unknown,
                  event: unknown,
                  payload: unknown,
                  dedupeKey?: string,
                ) => void;
                emit(product, event, payload ?? null, dedupeKey);
              }
            } catch {}
          },
          backoff: {
            baseMs: 1000,
            maxMs: 15000,
            maxAttempts: 1,
          },
        }}
        defaultOpen={false}
        onReset={handleClearThread}
        panelLeftMargin={leftMargin}
        elevation={askAiElevation}
        onClose={handleClose}
        conversationName={conversationName}
        onConversationNameChange={handleConversationNameChange}
        onMessageSent={handleMessageSent}
        onHistoryClick={() => setShowHistoryView(true)}
        showHistoryView={showHistoryView}
        onHistoryBack={() => setShowHistoryView(false)}
        historyViewTitle={t('Past_Conversations', 'Past Conversations')}
        historyViewContent={
          <PastConversationsView
            onSelectConversation={(id) => {
              setThreadIdInStore(id);
              setShowHistoryView(false);
            }}
            onSeeAll={() => {
              setShowHistoryView(false);
              chatPanelRef.current?.setOpen(false);
              history.push('/askai/conversations');
            }}
          />
        }
      />
    </>
  );
};

export interface AskAIChatHostProps {
  className?: string;
}

export const AskAIChatHost = ({className}: AskAIChatHostProps) => {
  useRequestToAutoAttach();
  return (
    <div
      id="chat-container"
      className={classify(css.panelWrapper, className)}
    ></div>
  );
};
export interface AskAIHeaderHostProps {
  className?: string;
  slot?: React.ReactNode;
}

export const AskAIHeaderHost = ({className, slot}: AskAIHeaderHostProps) => {
  useHeaderSlot(slot);
  return (
    <div
      id="chat-header-container"
      className={classify(css.headerWrapper, className)}
    ></div>
  );
};
